import css_bxyyd6toe1 from './css/ContentPlayer.css';
import Div from './Div.js';
import GetPrivateData from '../Classes/GetPrivateData.js';
import extendVideoJS from '../Functions/extendVideoJS.js';
import AdCompanionAd from './AdCompanionAd.js';

const css = css_bxyyd6toe1;

/** @namespace config.scripts.videojs */
/** @namespace videojs */

export default class ContentPlayer extends Div
{
	constructor(parentElement)
	{
		super(parentElement);

		// create the video element that will be used by videojs
		// IMPORTANT - do not refer to this element once videojs is initialized.
		// Use the videojs functions instead as this element
		let videoElement = this.element.ownerDocument.createElement('video');
		videoElement.classList.add('video-js');
		videoElement.classList.add('vjs-16-9');
		videoElement.classList.add('vjs-big-play-centered');
		this.element.appendChild(videoElement);

		this.companionAd = new AdCompanionAd(this);

		const privateData = getPrivateData.for(this);

		// event list used by videojsOn and videojsOff
		privateData.videojsEvents = {};

		// videojs will be instanced here
		privateData.videojs = null;

		// once videojs script is loaded
		privateData.videojsPromise = config.scripts.videojs
		.then
		(
			() =>
			{
				// extend the videojs library. videojs = window.videojs
				extendVideoJS(videojs);

				return new Promise
				(
					resolve =>
					{
						const videojsInstance = privateData.videojs = videojs
						(
							// element that will become a videojs instance
							videoElement,
							// videojs options
							{
								controls: true,
								playsinline: true,
								preload: 'auto',
								muted: true,
								fullscreenElement: config.player.element.querySelector('.mediaGroupContainer'),
								userActions:
								{
									doubleClick: function()
									{
										this.controlBar.fullscreenToggle.handleClick();
									}
								},
								controlBar:
								{
									pictureInPictureToggle: false
								},
								html5:
								{
									hls:
									{
										overrideNative: true,
										enableLowInitialPlaylist: true,
									},
									nativeAudioTracks: false,
									nativeVideoTracks: false,
								},
							},
							// videojs onReady
							() =>
							{
								// videojs and chrome bug. 2020-04-16
								// Chrome will fire mouseenter once when you enter, and again when any child elements are created under the mouse cursor.
								// videojs doesn't expect the second mouseenter event and will remove the control bar inactive timeout by overwriting a cached value with 0.
								// so we register a mouseenter and mouseleave event handler and only let one mouseenter event propagate
								let mouseEnter = false;
								videojsInstance.controlBar.el_.addEventListener
								(
									'mouseenter',
									e =>
									{
										if(mouseEnter)
										{
											e.stopImmediatePropagation();
										}
										else
										{
											mouseEnter = true;
										}
									},
									true
								);
								videojsInstance.controlBar.el_.addEventListener
								(
									'mouseleave',
									()=>
									{
										mouseEnter = false;
									},
									true
								);

								// todo: stop using raw element access once videojs supports
								//  setting this attribute through their own api or supports
								//  copying the attribute from the initial video element
								videojsInstance.tech_.el_.setAttribute('disablePictureInPicture','');

								videojsInstance.controlBar.addChild('SettingsToggle',{},videojsInstance.children_.length-1);

								const dispatchEvent = this.dispatchEvent.bind(this);

								const self = this;
								config.gestures.subscribe
								(
									function onGesture()
									{
										config.gestures.unsubscribe(onGesture);

										/**
										 * @config videojsInstance.paused Function
										 */

										if(videojsInstance.paused())
										{
											self.suppressVolumeEvent = (self.suppressVolumeEvent || 0) + 2;
											videojsInstance.muted(!videojsInstance.muted());
											videojsInstance.muted(!videojsInstance.muted());
										}
										else
										{
											videojsInstance.play();
										}

										dispatchEvent('gestured');
									}
								);
								// events that never need to be removed use vjs.on
								// events that need to be reset on load use videojsOn

								videojsInstance.on('play',onPlay.bind(this,videojsInstance));
								videojsInstance.on('pause',onPause.bind(this,videojsInstance));
								videojsInstance.on('ended',onEnded.bind(this));
								videojsInstance.on('timeupdate',onTimeupdate.bind(this,videojsInstance));
								videojsInstance.on('volumechange',onVolumechange.bind(this));

								videojsInstance.controlBar.volumePanel.muteToggle.on('unmute',event => event.stopPropagation() );

								resolve(videojsInstance);
							}
						);

						// now that videojs is in charge of the video element,
						// remove the reference to it. we should never use the
						// element directly, always use the videojs methods.
						// videojs likes to create it's own video element in place
						// so this one probably isn't valid anymore either
						videoElement = null;
					}
				);
			}
		);

		privateData.videojsPromise.then
		(
			() => this.dispatchEvent('ready')
		);
	}

	get cssText()
	{
		return css;
	}

	load(sources,poster)
	{
		const privateData = getPrivateData.for(this);

		return privateData.videojsPromise.then
		(
			videojs =>
			{
				const loadPromise = new Promise
				(
					(resolve,reject) =>
					{
						videojsOff.call(this);
						videojsOn.call(this,'durationchange',resolve);
						videojsOn.call(this,'loadedmetadata',resolve);
						videojsOn.call(this,'error',reject);
						videojs.src(sources);
						videojs.poster(poster);

						privateData.hasStarted = false;
						privateData.hasFinished = false;
						privateData.playing = false;
					}
				);

				loadPromise.then
				(
					() => this.dispatchEvent('load'),
					() => this.dispatchEvent('loadError')
				);

				return loadPromise;
			}
		);
	}

	playing(value)
	{
		const privateData = getPrivateData.for(this);

		if(arguments.length === 0)
		{
			return privateData.playing;
		}

		value = !!value;

		if(value === privateData.playing)
		{
			return Promise.resolve();
		}

		const result = new Promise
		(
			resolve =>
			{
				if(value)
				{
					this.dispatchEvent('playRequest');
					resolve(privateData.videojs.play());
				}
				else
				{
					resolve(privateData.videojs.pause());
				}
			}
		);

		if(value)
		{
			result.catch
			(
				reason =>
				{
					console.error('Attempt to start playing failed for reason: ',reason);
					this.dispatchEvent('playFailure');
				}
			);
		}

		return result;
	}

	volume(...args)
	{
		const videojs = getPrivateData.for(this).videojs;

		if(videojs)
		{
			return videojs.volume(...args);
		}
		else
		{
			return args.length ? undefined : 0;
		}
	}

	muted(...args)
	{
		const videojs = getPrivateData.for(this).videojs;

		if(videojs)
		{
			return videojs.muted(...args);
		}
		else
		{
			return args.length ? undefined : true;
		}
	}

	get proxy()
	{
		const privateData = getPrivateData.for(this);

		return privateData.proxy ||
		(
			privateData.proxy =
			{
				get playedTotal()
				{
					if(!privateData.videojs)
					{
						return 0;
					}
					const played = privateData.videojs.played();
					const length = played.length;
					let total = 0;
					for(let i = 0; i < length; ++i)
					{
						total += played.end(i) - played.start(i);
					}
					return total;
				},
				get currentTime()
				{
					if(!privateData.videojs)
					{
						console.error('proxy used before initialization');
						return 0;
					}
					// return privateData.videojs.currentTime();
					if(!privateData.videojs.scrubbing())
					{
						privateData.proxyCurrentTime = privateData.videojs.currentTime();
					}
					return privateData.proxyCurrentTime;
				},
				get duration()
				{
					console.warn('duration queried?');
					if(!privateData.videojs)
					{
						console.error('proxy used before initialization');
						return 0;
					}
					return privateData.videojs.duration();
				},
				get playsinline()
				{
					if(!privateData.videojs)
					{
						console.error('proxy used before initialization');
						return true;
					}
					return privateData.videojs.playsinline();
				},
				getAttribute(value)
				{
					if(!privateData.videojs)
					{
						console.error('proxy used before initialization');
						return null;
					}

					if(value !== 'playsinline')
					{
						console.warn(`attribute ${value} queried?`);
					}

					return privateData
					.videojs
					.tech({IWillNotUseThisInPlugins: true})
					.getAttribute(value);
				}
			}
		);
	}
}

ContentPlayer.eventList =
[
	'ready','gestured',
	'load','loadError','start','finish',
	'play','pause','volumechange','timeupdate'
];

//
const getPrivateData = new GetPrivateData(ContentPlayer);
// const getProtectedData = new GetProtectedData(ContentPlayer);


/*
	Videojs's broken event handling means we don't use videojs.on and
	videojs.off. Instead, we use videojsOn and videojsOff. These functions will
	keep an internal list of callbacks and copy the videojs.on api. The
	difference, is that while videojs.off() will wipe out necessary events for
	the player to function, videojsOff() will only wipe out user events.
	Similarly, videojsOff('someEvent') will wipe out only user callbacks for
	that event.
*/

function videojsOn(events,callback)
{
	const privateData = getPrivateData.for(this);
	const videojsEvents = privateData.videojsEvents;
	const videojs = privateData.videojs;

	events = [].concat(events);
	events.forEach
	(
		event =>
		{
			videojsEvents[event] = videojsEvents[event] || [];
			videojsEvents[event].push(callback);
			videojs.on(event,callback);
		}
	)
}

function videojsOff(events,callback)
{
	const privateData = getPrivateData.for(this);
	const videojsEvents = privateData.videojsEvents;
	const videojs = privateData.videojs;

	events = [].concat(events || Object.keys(videojsEvents));
	events.forEach
	(
		event =>
		{
			const callbacks = callback ? [callback] : (videojsEvents[event] || []);
			callbacks.forEach
			(
				callback =>
				{
					videojs.off(event,callback);
					const index = (videojsEvents[event]||[]).indexOf(callback);
					if(index === -1)
					{
						return;
					}
					videojsEvents[event].splice(index,1);
				}
			);
		}
	);
}

function onPlay(videojs)
{
	const privateData = getPrivateData.for(this);
	privateData.playing = true;
	if(!privateData.hasStarted)
	{
		privateData.hasStarted = true;
		this.dispatchEvent('start');
	}
	this.dispatchEvent('play',{detail:{currentTime:videojs.currentTime()}});
}

function onPause(videojs)
{
	const privateData = getPrivateData.for(this);
	privateData.playing = false;
	this.dispatchEvent
	(
		'pause',
		{
			detail:
			{
				scrubbing: videojs.scrubbing(),
				ended: videojs.ended()
			}
		}
	);
}

function onEnded()
{
	const privateData = getPrivateData.for(this);

	if(!privateData.hasFinished)
	{
		privateData.hasFinished = true;
		this.dispatchEvent('finish');
	}
}

function onTimeupdate(videojs)
{
	this.dispatchEvent
	(
		'timeupdate',
		{
			detail:
			{
				currentTime: videojs.currentTime(),
				duration: videojs.duration()
			}
		}
	);
}

function onVolumechange()
{
	if(this.suppressVolumeEvent)
	{
		if(!(--this.suppressVolumeEvent))
		{
			delete this.suppressVolumeEvent;
		}
		return;
	}

	this.dispatchEvent('volumechange');
}
